// src/pages/RGPList.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Modal from '../../components/Modal';
import RGPForm from './RGPForm';
import api, { createRGP, deleteRGP, updateRGP } from '../../services/api';
import Button from '../ui/Button';
import * as MdIcons from 'react-icons/md'
import { toast } from 'react-toastify';
import DeleteConfirmationModal from '../sections/DeleteConfirmationModal';
import { HelmetAsync } from '../../utils/HelmetAsync';

const RGPList = () => {
    const [rgps, setRgps] = useState([]);
    const [mode, setMode] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRgp, setEditingRgp] = useState(null);
    const [deletingItemId, setDeletingItemId] = useState(null);
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        status: '',
        type: '',
        startDate: '',
        endDate: ''
    });

    useEffect(() => {
        fetchRgps();
    }, [filters]);

    const fetchRgps = async () => {
        setLoading(true);

        try {
        const params = new URLSearchParams();
        if (filters.status) params.append('status', filters.status);
        if (filters.type) params.append('type', filters.type);
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        
        const response = await api.get(`/rgp?${params.toString()}`);
        setRgps(response.data);
        } catch (error) {
            toast.error(error.message || 'Failed to fetch data');
            // console.error('Error fetching RGP records:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (formData) => {
        try {
        if (editingRgp) {
            await updateRGP(editingRgp._id,formData);
            setIsModalOpen(false);
            toast.success('RGP updated successfully!');
        } else {
            await createRGP(formData);
            setIsModalOpen(false);
            toast.success('RGP created successfully!');
        }
        fetchRgps();
        setIsModalOpen(false);
        setEditingRgp(null);
        } catch (error) {
            console.error('Error saving RGP:', error);
            toast.error('RGP saving failed!');
        }
    };

    const options = {
        year: '2-digit',
        month: 'long',
        day: '2-digit',
    };

    // Determine status based on dates
    const getStatus = (item) => {
        const today = new Date();
        const openingDate = new Date(item.openingDate);
        const expectedClosureDate = new Date(item.expectedClosureDate);
        const closureDate = item.closureDate ? new Date(item.closureDate) : null;

        if (closureDate) {
        return 'Completed';
        } else if (today > expectedClosureDate) {
        return 'Delayed';
        } else if (today >= openingDate) {
        return 'In Progress';
        } else {
        return 'Open';
        }
    };

    const userRole = localStorage.getItem('userRole');
    const title = `RGP - BreyERP`;

    return (
    <>
        <HelmetAsync title={title} />
        <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl font-bold">Returnable Gate Pass</h1>
            <Button
                // onClick={() => setIsModalOpen(true)}
                onClick={() => {
                    setMode('create');
                setIsModalOpen(true);
                }}
            >
                <MdIcons.MdAdd className='inline mb-1'/> New RGP
            </Button>
        </div>
        
        <div className="bg-white dark:bg-gray-900 shadow-md rounded-lg px-3 py-2 mb-3">
            <div className="flex items-center justify-between flex-col md:flex-row">
                <div>
                    <label htmlFor="status" className="inline text-sm font-medium text-gray-700 pr-2 mb-0">
                    Status
                    </label>
                    <select
                    name="status"
                    id="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                    className="w-28 bg-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                    <option value="">All Statuses</option>
                    {/* <option value="Open">Open</option> */}
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Delayed">Delayed</option>
                    </select>
                    <label htmlFor="type" className="text-sm font-medium text-gray-700 px-2 mb-0">
                    Type
                    </label>
                    <select
                    name="type"
                    id="type"
                    value={filters.type}
                    onChange={handleFilterChange}
                    className="w-28 bg-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                    <option value="">All Types</option>
                    <option value="inward">Inward</option>
                    <option value="outward">Outward</option>
                    </select>
                </div>
                
                <div>
                    <label htmlFor="startDate" className="text-sm font-medium text-gray-700 pr-2 mb-0">
                    From
                    </label>
                    <input
                    type="date"
                    name="startDate"
                    id="startDate"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                    className="w-36 bg-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    <label htmlFor="endDate" className="text-sm font-medium text-gray-700 px-2 mb-0">
                    To
                    </label>
                    <input
                    type="date"
                    name="endDate"
                    id="endDate"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                    className="w-36 bg-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
            </div>
        </div>
        
        <Modal
            isOpen={isModalOpen}
            onClose={() => {
                setIsModalOpen(false);
                setEditingRgp(null);
                setMode(null);
            }}
            title={
                mode === 'view' ? 'View RGP' : 
                mode === 'edit' ? 'Edit RGP' : 'Create New RGP'
            }
        >
            <RGPForm
            onSubmit={handleSubmit}
            initialData={editingRgp || {}}
            onCancel={() => {
                setIsModalOpen(false);
                setEditingRgp(null);
                setMode(null);
            }}
            edit={editingRgp}
            mode={mode}
            />
        </Modal>
        
        {/* <div className="overflow-x-auto"> */}
        {loading ? (
            <>
            <div className="text-center my-5 py-5 h-96">
                <div role="status" className='mt-5 pt-5'>
                    <svg aria-hidden="true" className="inline w-16 h-16 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                    </svg>
                    <span className="sr-only">Loading...</span>
                </div>
            <p className="text-gray-700 text-xl text-center p-6">Loading...</p>
            </div>
            </>
        ) : (
        <div className="flex relative flex-column bg-white shadow-md rounded-lg h-[calc(90vh-108px)] overflow-auto">

            <table className="w-full">
            <thead className="bg-gray-200">
                <tr>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        RGP Number
                    </th>
                    <th className="px-2 py-3 text-left whitespace-nowrap text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Customer/Vendor
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Challan No.
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Bill No.
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                        Type
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Status
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                        Open Date
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                        Expected By
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Actions
                    </th>
                </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                {rgps.map((rgp) => {
                const status = getStatus(rgp);
                return (
                <tr key={rgp._id} className='hover:bg-sky-100'>
                    <td className="px-2 py-1.5 text-sm font-medium text-gray-900">
                    {rgp.rgpNumber}
                    </td>
                    <td className="px-2 py-1.5 text-sm text-gray-500">
                    {rgp.customerName || rgp.vendorName}
                    </td>
                    <td className="px-2 py-1.5 text-sm text-gray-500">
                    {rgp.challanNumber}
                    </td>
                    <td className="px-2 py-1.5 text-sm text-gray-500">
                    {rgp.billNumber}
                    </td>
                    {/* <td className="px-2 py-1.5 text-sm text-gray-500">
                        {rgp.natureOfWork ?
                        <>
                        {rgp.natureOfWork.map((nature, idx) => (
                            <span key={`std-${idx}`} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {nature.split(/(?=[A-Z])/).join(' ')}
                            </span>
                        ))}
                        </>
                        : <></>
                        }
                    {
                    Object.entries(rgp.natureOfWork || {})
                    .filter(([_, value]) => value)
                    .map(([key]) => key)
                    .join(', ')
                    }
                    </td> */}
                    <td className="px-2 py-1.5 text-sm text-gray-500">
                    {rgp.customerName ? 'Inward' : 'Outward'}
                    </td>
                    <td className="px-2 py-1.5">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full shadow-sm ${getStatusBadgeColor(status)}`}>
                        {status}
                    </span>
                    </td>
                    <td className="px-2 py-1.5 text-xs text-gray-500">
                    {new Date(rgp.openingDate).toLocaleDateString('en-IN', options)}
                    </td>
                    {status === 'Delayed' ?
                    <td className="px-2 py-1.5 text-xs font-bold text-red-500">
                        {new Date(rgp.expectedClosureDate).toLocaleDateString('en-IN', options)}
                    </td>
                    :
                    <td className="px-2 py-1.5 text-xs text-gray-500">
                        {new Date(rgp.expectedClosureDate).toLocaleDateString('en-IN', options)}
                    </td>
                    }
                    <td className="px-2 py-1.5 whitespace-nowrap text-center text-sm font-medium">
                        <Button variant="outline" size="sm"
                            onClick={() => {
                                setMode('view');
                            setEditingRgp(rgp);
                            setIsModalOpen(true);
                            }}
                        >
                            <MdIcons.MdOutlineRemoveRedEye className="h-3 w-3" />
                            <span className="sr-only">View</span>
                        </Button>
                        <Button size="sm"
                            onClick={() => {
                                setMode('edit');
                            setEditingRgp(rgp);
                            setIsModalOpen(true);
                            }}
                        >
                            <MdIcons.MdEditSquare className="h-3 w-3" />
                            <span className="sr-only">Edit</span>
                        </Button>
                        {userRole !== 'item_user' ? (
                        <Button variant="destructive" size="sm" onClick={() => setDeletingItemId(rgp._id)}>
                            <MdIcons.MdDeleteForever className="h-3 w-3" />
                            <span className="sr-only">Delete</span>
                        </Button>
                        ) : (
                        <></>
                        )}


                    {/* <Link
                        to={`/rgp/${rgp.rgpNumber}`}
                        className="text-indigo-600 hover:text-indigo-900"
                    >
                        View
                    </Link> */}
                    </td>
                </tr>
                )})}
            </tbody>
            </table>
        </div>
        )}

        {deletingItemId && (
            <DeleteConfirmationModal
            method={deleteRGP}
            itemId={deletingItemId}
            onClose={() => setDeletingItemId(null)}
            onConfirm={() => {
                // onDelete(deletingItemId);
                fetchRgps();
                setDeletingItemId(null);
            }}
            />
        )}

    </>
    );
};

function getStatusBadgeColor(status) {
  switch (status) {
    case 'Completed': return 'bg-green-100 text-green-800';
    case 'In Progress': return 'bg-blue-100 text-blue-800';
    case 'Delayed': return 'bg-red-100 text-red-800';
    default: return 'bg-yellow-100 text-yellow-800';
  }
}

export default RGPList;